import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';

/**
 * Modal component for creating a new project
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {() => void} props.onClose - Function to call when modal is closed
 * @param {(title: string) => Promise<void>} props.onCreateProject - Function to call when project is created
 */
export default function NewProjectModal({ isOpen, onClose, onCreateProject }) {
    const [projectName, setProjectName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!projectName.trim()) {
            setError('Project name is required');
            return;
        }

        setIsCreating(true);
        setError('');

        try {
            await onCreateProject(projectName.trim());
            setProjectName('');
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create project');
        } finally {
            setIsCreating(false);
        }
    };

    const handleClose = () => {
        if (!isCreating) {
            setProjectName('');
            setError('');
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[425px] bg-background border-border">
                <DialogHeader>
                    <DialogTitle className="text-foreground">Create New Project</DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                        Enter a name for your new project. You can organize related chats within this project.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="py-4">
                        <label
                            htmlFor="project-name"
                            className="block text-sm font-medium text-foreground mb-2"
                        >
                            Project Name
                        </label>
                        <input
                            id="project-name"
                            type="text"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            placeholder="e.g., Chest X-Ray Analysis"
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            disabled={isCreating}
                            autoFocus
                        />
                        {error && (
                            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
                        )}
                    </div>

                    <DialogFooter className="flex-row justify-end gap-2 sm:gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClose}
                            disabled={isCreating}
                            className="flex-1 sm:flex-none py-2 h-auto text-sm bg-transparent hover:bg-muted text-foreground border-border"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isCreating || !projectName.trim()}
                            className="flex-1 sm:flex-none py-2 h-auto text-sm bg-primary hover:opacity-90 text-white disabled:opacity-50 disabled:cursor-not-allowed border-none"
                        >
                            {isCreating ? 'Creating...' : 'Create Project'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
